import React from "react";
import {
  FaEnvelope,
  FaArrowLeft,
  FaLock,
  FaCheckCircle,
  FaExclamationCircle,
} from "react-icons/fa";
import { Turnstile } from "@marsidev/react-turnstile";
import { SignInLink } from "../utility/routeUtils";
import { useForgotPasswordForm } from "../hooks/useForgotPasswordForm";
import { EMAIL_REGEX } from "../utils/validation";

export default function ForgotPasswordForm() {
  const {
    email,
    loading,
    success,
    error,
    resendCountdown,
    captchaToken,
    handleChange,
    handleSubmit,
    handleResend,
    handleCaptchaError,
    handleCaptchaExpire,
    handleCaptchaVerify,
  } = useForgotPasswordForm();

  if (success) {
    return (
      <section className="min-h-screen bg-brand-cream flex items-center justify-center p-4">
        <div className="max-w-md w-full space-y-8">
          {/* Success Card */}
          <div className="card-board p-8">
            <div className="text-center">
              {/* Success Icon */}
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-lg bg-brand-charcoal mb-6">
                <FaCheckCircle className="h-8 w-8 text-brand-cream" />
              </div>

              {/* Success Content */}
              <h2 className="heading-display text-3xl mb-3">
                Check your email
              </h2>
              <p className="text-lg text-brand-muted mb-6 leading-relaxed">
                We've sent password reset instructions to{" "}
                <span className="font-semibold text-brand-charcoal">{email}</span>
              </p>

              {/* Action Buttons */}
              <div className="space-y-4">
                <p className="text-sm text-gray-500">
                  Didn't receive the email? Check your spam folder or{" "}
                  <button
                    onClick={handleResend}
                    disabled={resendCountdown > 0 || loading}
                    className={`font-semibold transition-colors duration-200 ${
                      resendCountdown > 0 || loading
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-brand-charcoal hover:underline"
                    }`}
                  >
                    {loading
                      ? "Sending..."
                      : resendCountdown > 0
                      ? `Try again in ${resendCountdown}s`
                      : "try again"}
                  </button>
                  {resendCountdown === 0 && !loading && (
                    <span className="block text-xs text-gray-400 mt-1">
                      (You'll need to complete CAPTCHA verification again)
                    </span>
                  )}
                </p>

                <SignInLink
                  to="/signin"
                  className="inline-flex items-center justify-center w-full px-6 py-3 text-sm font-display font-bold tracking-wide text-brand-charcoal bg-brand-cream border border-brand-charcoal/20 rounded-lg hover:border-brand-gold-ink"
                >
                  <FaArrowLeft className="mr-2" />
                  Back to sign in
                </SignInLink>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen bg-brand-cream flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8">
        {/* Main Form Card */}
        <div className="card-board p-8">
          {/* Header */}
          <div className="text-center mb-8">
            {/* Icon */}
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-lg bg-brand-charcoal mb-6">
              <FaLock className="h-8 w-8 text-brand-cream" />
            </div>

            {/* Title & Description */}
            <h1 className="heading-display text-3xl mb-3">
              Reset your password
            </h1>
            <p className="text-lg text-brand-muted leading-relaxed">
              Enter your email address and we'll send you instructions to reset
              your password.
            </p>
          </div>

          {/* Form */}
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Email Input */}
            <div className="space-y-2">
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FaEnvelope className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={handleChange}
                  className={`block w-full pl-12 pr-4 py-4 border rounded-lg text-brand-charcoal placeholder-brand-mist focus:outline-none focus:ring-2 focus:ring-brand-gold-ink focus:border-transparent ${
                    email && !error && !loading && EMAIL_REGEX.test(email)
                      ? "border-brand-charcoal/20"
                      : error
                      ? "border-red-300 bg-red-50"
                      : "border-brand-charcoal/20"
                  }`}
                  placeholder="Enter your email address"
                  autoFocus
                />
                {email && !error && !loading && EMAIL_REGEX.test(email) && (
                  <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                    <FaCheckCircle className="h-5 w-5 text-brand-charcoal" />
                  </div>
                )}
              </div>
              {email && !EMAIL_REGEX.test(email) && (
                <p className="text-xs text-red-500">
                  Please enter a valid email address
                </p>
              )}
            </div>

            {/* CAPTCHA */}
            <div className="space-y-2">
              <div className="flex justify-center">
                <Turnstile
                  siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                  onSuccess={handleCaptchaVerify}
                  onError={handleCaptchaError}
                  onExpire={handleCaptchaExpire}
                />
              </div>
              {captchaToken && (
                <div className="flex items-center justify-center space-x-2 text-sm text-brand-charcoal">
                  <FaCheckCircle className="h-4 w-4" />
                  <span>Security verification complete</span>
                </div>
              )}
            </div>

            {/* Error Display */}
            {error && (
              <div className="rounded-xl bg-red-50 border border-red-200 p-4 transform transition-all duration-300 animate-fade-in">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <FaExclamationCircle className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700 font-medium">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !captchaToken}
              className={`group relative w-full flex justify-center items-center py-4 px-6 text-base font-display font-bold tracking-wide rounded-lg ${
                loading || !captchaToken
                  ? "bg-brand-mist text-brand-charcoal cursor-not-allowed"
                  : "bg-brand-gold text-brand-charcoal hover:bg-brand-gold/90"
              }`}
            >
              {loading ? (
                <div className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-brand-charcoal"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Sending...
                </div>
              ) : (
                "Send reset instructions"
              )}
            </button>

            {/* Back to Sign In */}
            <div className="text-center pt-4">
              <SignInLink
                to="/signin"
                className="inline-flex items-center justify-center text-sm font-medium text-brand-muted hover:text-brand-charcoal transition-colors duration-200 hover:underline"
              >
                <FaArrowLeft className="mr-2" />
                Back to sign in
              </SignInLink>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
